import { useQueryClient } from '@tanstack/react-query';
import { default as Form } from 'antd/es/form';
import { default as Modal } from 'antd/es/modal';
import { useMemo, useState } from 'react';

import type { PostCustomerUsersBodyData } from 'api';
import { useGetCustomerRoles, usePostCustomerUsers } from 'api';

import { useOrganisationId } from 'hooks/context';
import { useSafeRoleOptions } from 'hooks/useRolesOptions';

import { UserAddIcon } from '@atoms/Icon/UserAddIcon';

import { NewUserDialogForm } from './NewUserDialogForm';
import { NewUserFeedback } from './NewUserFeedback';
import { userManagementNewUserT as translations } from './translations';

import {
  NEW_USER_FEEDBACK_MODAL_TEST_ID,
  NEW_USER_DIALOG_TEST_ID
} from '../constants';
import { FeedbackModal } from '../shared/FeedbackModal';

type Props = {
  open: boolean;
  onClose: (value: boolean) => void;
};

export type ResultState =
  | { open: false }
  | {
      open: true;
      title: string;
      content: React.ReactNode;
      closeDialogOnClose?: boolean;
    };

const asFormErrors = (validationErrors: Record<string, string[]>) =>
  Object.keys(validationErrors).map((field) => ({
    name: field,
    errors: validationErrors[field]
  }));

export const NewUserDialog = ({ open, onClose }: Props) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const organisationId = useOrganisationId<number>();

  const [result, setResult] = useState<ResultState>({ open: false });

  const onDialogClose = (value: boolean) => {
    form.resetFields();
    onClose(value);
  };

  const { data: { data: rolesData } = {} } =
    useGetCustomerRoles(organisationId);
  const roles = rolesData || [];

  const selectedRole = Form.useWatch('role', form);

  const { options: safeChoices } = useSafeRoleOptions({
    roles,
    currentRole: ''
  });

  const roleOptions = useMemo(
    () => safeChoices.filter((o) => o.value !== ''),
    [safeChoices]
  );

  const mutation = usePostCustomerUsers();

  const closeResultModal = () => {
    const shouldCloseDialog =
      result.open &&
      'closeDialogOnClose' in result &&
      result.closeDialogOnClose;

    setResult({ open: false });

    if (shouldCloseDialog) onDialogClose(false);
  };

  const onFinish = async (values: PostCustomerUsersBodyData) => {
    try {
      const { data: createdUser } = await mutation.mutateAsync({
        data: { data: values },
        organisationId
      });

      queryClient.invalidateQueries({
        queryKey: ['users', organisationId],
        refetchType: 'active'
      });

      if (Boolean(createdUser?.active) === false) {
        setResult(NewUserFeedback.deactivatedDetected());
        return;
      }

      setResult(NewUserFeedback.success());
    } catch (error: any) {
      const validationErrors = error?.error?.details?.attributes;
      if (validationErrors) {
        form.setFields(asFormErrors(validationErrors));
        setResult(NewUserFeedback.reviewForm());
        return;
      }

      setResult(NewUserFeedback.genericError());
    }
  };

  return (
    <>
      <Modal
        open={open}
        data-testid={NEW_USER_DIALOG_TEST_ID}
        onCancel={() => onDialogClose(false)}
        title={
          <div className="flex items-center gap-3 pb-5 text-xl">
            <UserAddIcon />
            <div className="font-medium">
              {translations.createUserDialog.title}
            </div>
          </div>
        }
        footer={null}
        width={600}
      >
        <NewUserDialogForm
          form={form}
          onFinish={onFinish}
          onCancel={() => onDialogClose(false)}
          roleOptions={roleOptions}
          selectedRole={selectedRole}
          isSubmitting={mutation.isPending}
        />
      </Modal>

      {result.open && (
        <FeedbackModal
          open={result.open}
          testId={NEW_USER_FEEDBACK_MODAL_TEST_ID}
          title={result.title}
          message={result.content}
          onClose={closeResultModal}
        />
      )}
    </>
  );
};
